import { useEffect, useLayoutEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useOverlayAnimation } from '@/hooks/useOverlayAnimation';
import { CloseIcon } from '@/components/ui/Icons';
import { OverlayScrollArea } from '@/components/ui/OverlayScrollArea/OverlayScrollArea';
import { OverlayIntrinsicSize } from '@/components/ui/OverlayIntrinsicSize/OverlayIntrinsicSize';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  /** Размер: sm (по умолчанию), md, ml (между md и lg), lg */
  size?: 'sm' | 'md' | 'ml' | 'lg';
  /** Дополнительный класс для контейнера модалки */
  className?: string;
  /** Заголовок как остров (как у поповеров) с кнопкой закрыть */
  headerVariant?: 'default' | 'island';
  /**
   * default — обычная шапка с заголовком.
   * close-only — без заголовка, только кнопка закрыть поверх контента (правый верх).
   */
  headerMode?: 'default' | 'close-only';
  /** Подпись для aria-label диалога при headerMode="close-only" */
  contentAriaLabel?: string;
  /**
   * Заменяет текстовый заголовок в шапке island (например кнопка).
   * При задании для role="dialog" используется aria-label из dialogAriaLabel или title.
   */
  islandTitleSlot?: ReactNode;
  /** Явная подпись диалога для a11y, если islandTitleSlot не текстовый заголовок */
  dialogAriaLabel?: string;
  /** false — тело без OverlayScrollbars (например на всю высоту с iframe внутри) */
  scrollableBody?: boolean;
  /**
   * Оверлей position:absolute внутри ближайшего position:relative предка (например chatShell),
   * а не fixed на весь viewport — нужно для модалок в панели мессенджера.
   */
  containedOverlay?: boolean;
  /** Доп. класс на корневой `.modal-overlay` (например z-index поверх попапов таблицы). */
  overlayClassName?: string;
}

export function Modal({
  open,
  onClose,
  title,
  children,
  size = 'sm',
  className = '',
  headerVariant = 'default',
  headerMode = 'default',
  contentAriaLabel,
  islandTitleSlot,
  dialogAriaLabel,
  scrollableBody = true,
  containedOverlay = false,
  overlayClassName = '',
}: ModalProps) {
  const { shouldRender, backdropClasses, contentClasses, requestClose } = useOverlayAnimation(open, onClose);
  const mousedownOnBackdropRef = useRef(false);

  useLayoutEffect(() => {
    if (!shouldRender || containedOverlay) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [shouldRender, containedOverlay]);

  useEffect(() => {
    if (!shouldRender) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') requestClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [shouldRender, requestClose]);

  if (!shouldRender) return null;

  const islandHeader = headerVariant === 'island' && headerMode === 'default';
  const islandUsesSlot = islandHeader && islandTitleSlot != null;

  const headerClass =
    headerMode === 'close-only'
      ? 'modal__header modal__header_close-only'
      : islandHeader
        ? 'modal__header modal__header_island'
        : 'modal__header';

  const modalShellClass = [
    `modal modal_${size}`,
    contentClasses,
    className,
    headerMode === 'close-only' ? 'modal--chromeless' : '',
  ]
    .filter(Boolean)
    .join(' ')
    .trim();

  const bodyClassName = [
    'modal__body',
    headerMode === 'close-only' ? 'modal__body_flush modal__body_fill' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const dialogA11y =
    headerMode === 'close-only'
      ? { 'aria-label': contentAriaLabel ?? (title.trim() ? title : 'Диалог') }
      : islandUsesSlot
        ? { 'aria-label': dialogAriaLabel ?? title }
        : { 'aria-labelledby': 'modal-title' };

  const closeBtn = (
    <button
      type="button"
      className="modal__close"
      onClick={requestClose}
      aria-label="Закрыть"
      title="Закрыть"
    >
      <CloseIcon size={20} />
    </button>
  );

  const modalShell = (
    <div className={modalShellClass} onClick={(e) => e.stopPropagation()}>
      <div className={headerClass} id={headerMode === 'close-only' ? undefined : 'modal-title'}>
        {headerMode === 'close-only' ? (
          closeBtn
        ) : islandHeader ? (
          <>
            <span className="modal__header-title">{islandTitleSlot ?? title}</span>
            {closeBtn}
          </>
        ) : (
          title
        )}
      </div>
      {scrollableBody ? (
        <OverlayScrollArea className={bodyClassName} defer={false}>
          {children}
        </OverlayScrollArea>
      ) : (
        <div className={bodyClassName}>{children}</div>
      )}
    </div>
  );

  const overlay = (
    <div
      className={`modal-overlay ${backdropClasses}${containedOverlay ? ' modal-overlay--contained' : ''} ${overlayClassName}`.trim()}
      onMouseDown={(e) => {
        mousedownOnBackdropRef.current = e.target === e.currentTarget;
      }}
      onMouseUp={(e) => {
        if (mousedownOnBackdropRef.current && e.target === e.currentTarget) {
          requestClose();
        }
      }}
      role="dialog"
      aria-modal="true"
      {...dialogA11y}
    >
      {containedOverlay ? (
        <div className="modal__containedSizer">{modalShell}</div>
      ) : (
        <OverlayIntrinsicSize variant="centered">{modalShell}</OverlayIntrinsicSize>
      )}
    </div>
  );

  if (containedOverlay) {
    return overlay;
  }
  return createPortal(overlay, document.body);
}
